import { useState, useEffect } from 'react';
import { createAPI } from 'edge-stream-js';
import type { GroupInfo } from 'edge-stream-js';
import { useToast } from 'edge-stream-js-react';
import { ErrorBoundary } from './components';
import { UsersIcon, BuildingIcon, CalendarIcon, ClockIcon, UserIcon } from '@bizfirst/common-react';

interface UserDashboardProps {
  apiBaseUrl: string;
  currentUser: string;
  accessToken?: string;
}

export function UserDashboard({ apiBaseUrl, currentUser, accessToken }: UserDashboardProps) {
  const api = createAPI(apiBaseUrl, accessToken ? () => accessToken : undefined);
  const toast = useToast();
  const [activeGroups, setActiveGroups] = useState<GroupInfo[]>([]);
  const [selectedGroupDetails, setSelectedGroupDetails] = useState<GroupInfo | null>(null);
  const [loading, setLoading] = useState(false);

  // Load user groups on mount and periodically
  useEffect(() => {
    loadUserGroups();
    const interval = setInterval(loadUserGroups, 5000);
    return () => clearInterval(interval);
  }, [currentUser]);

  const loadUserGroups = async () => {
    try {
      const userMemberships = await api.getUserGroups(currentUser);

      if (!userMemberships || userMemberships.length === 0) {
        setActiveGroups([]);
        return;
      }

      const groupsMap = new Map<string, GroupInfo>();

      for (const membership of userMemberships) {
        const key = `${membership.serverId}:${membership.groupName}`;
        if (!groupsMap.has(key)) {
          const details = await api.getGroupDetails(membership.serverId, membership.groupName);
          if (details) {
            groupsMap.set(key, details);
          }
        }
      }

      setActiveGroups(Array.from(groupsMap.values()));
    } catch (error) {
      console.error('Error loading user groups:', error);
      setActiveGroups([]);
    }
  };

  const handleLeaveGroup = async (group: GroupInfo) => {
    if (confirm(`Leave group ${group.groupName}?`)) {
      setLoading(true);
      try {
        const success = await api.leaveGroup(group.serverId, group.groupName, currentUser);

        if (success) {
          setActiveGroups(activeGroups.filter(
            g => !(g.serverId === group.serverId && g.groupName === group.groupName)
          ));

          if (selectedGroupDetails?.groupId === group.groupId) {
            setSelectedGroupDetails(null);
          }
          toast.success('Left group successfully');
        } else {
          toast.warning('Could not leave group - please try again');
        }
      } catch (error) {
        toast.error(`Error leaving group: ${error instanceof Error ? error.message : String(error)}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleViewDetails = async (group: GroupInfo) => {
    setLoading(true);
    try {
      const details = await api.getGroupDetails(group.serverId, group.groupName);
      if (details) {
        setSelectedGroupDetails(details);
      }
    } catch (error) {
      console.error('Error loading group details:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="dashboard-container">
        <h2>My Groups</h2>

        <div className="user-info">
        <p>
          <strong>User ID:</strong> <code>{currentUser}</code>
        </p>
      </div>

      <div className="dashboard-layout">
        {/* Active Groups */}
        <div className="groups-panel full-width">
          <h3>Active Groups ({activeGroups.length})</h3>

          {activeGroups.length === 0 ? (
            <div className="empty-state">
              <p>You haven't joined any groups yet</p>
              <p>Go to <strong>Servers</strong> dashboard to browse and join groups</p>
            </div>
          ) : (
            <div className="groups-grid">
              {activeGroups.map((group) => (
                <div key={group.groupId} className="group-detail-card">
                  <div className="group-header">
                    <h4>{group.groupName}</h4>
                    {group.description && <p className="description">{group.description}</p>}
                  </div>

                  <div className="group-metadata">
                    <div className="metadata-item">
                      <UsersIcon />
                      <span className="label">Members:</span>
                      <span className="value">{group.memberCount}</span>
                    </div>
                    <div className="metadata-item">
                      <BuildingIcon />
                      <span className="label">Server:</span>
                      <span className="value">{group.serverId}</span>
                    </div>
                    <div className="metadata-item">
                      <CalendarIcon />
                      <span className="label">Created:</span>
                      <span className="value">{new Date(group.createdAt).toLocaleDateString()}</span>
                    </div>
                    {group.createdBy && (
                      <div className="metadata-item">
                        <UserIcon />
                        <span className="label">Created by:</span>
                        <span className="value">{group.createdBy}</span>
                      </div>
                    )}
                    {group.lastActivityAt && (
                      <div className="metadata-item">
                        <ClockIcon />
                        <span className="label">Last Activity:</span>
                        <span className="value">{new Date(group.lastActivityAt).toLocaleTimeString()}</span>
                      </div>
                    )}
                  </div>

                  <div className="group-members">
                    <p className="members-label">Members ({(group.members || []).length}):</p>
                    <div className="member-list">
                      {(group.members || []).slice(0, 5).map((member, idx) => (
                        <span key={idx} className="member-badge" title={member}>
                          {member}
                        </span>
                      ))}
                      {(group.members || []).length > 5 && (
                        <span className="member-badge">+{(group.members || []).length - 5}</span>
                      )}
                    </div>
                  </div>

                  <div className="group-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleViewDetails(group)}
                      disabled={loading}
                    >
                      View Details
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleLeaveGroup(group)}
                      disabled={loading}
                    >
                      Leave Group
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Group Details */}
        {selectedGroupDetails && (
          <div className="details-panel">
            <h3>Group Details</h3>
            <div className="details-content">
              <div className="detail-item">
                <label>Name:</label>
                <p>{selectedGroupDetails.groupName}</p>
              </div>

              {selectedGroupDetails.description && (
                <div className="detail-item">
                  <label>Description:</label>
                  <p>{selectedGroupDetails.description}</p>
                </div>
              )}

              <div className="detail-item">
                <label>Server ID:</label>
                <p><code>{selectedGroupDetails.serverId}</code></p>
              </div>

              <div className="detail-item">
                <label>Members:</label>
                <p>{selectedGroupDetails.memberCount}</p>
              </div>

              <div className="detail-item">
                <label>Created:</label>
                <p>{new Date(selectedGroupDetails.createdAt).toLocaleString()}</p>
              </div>

              {selectedGroupDetails.createdBy && (
                <div className="detail-item">
                  <label>Created By:</label>
                  <p>{selectedGroupDetails.createdBy}</p>
                </div>
              )}

              {selectedGroupDetails.lastActivityAt && (
                <div className="detail-item">
                  <label>Last Activity:</label>
                  <p>{new Date(selectedGroupDetails.lastActivityAt).toLocaleString()}</p>
                </div>
              )}

              <button
                className="btn btn-secondary"
                onClick={() => setSelectedGroupDetails(null)}
              >
                Close
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </ErrorBoundary>
  );
}
